"use client"
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaCheck, FaCheckDouble, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useMyNotifications, useMarkNotificationAsRead, useMarkAllNotificationsAsRead } from '@/graphql/notifications';
import { toast } from 'react-toastify';
import Image from 'next/image';
import Link from 'next/link';
import { formatDate } from '@/lib/utils';
import { useLocale, useTranslations } from 'next-intl';

const NotificationDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [markingId, setMarkingId] = useState<string | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const {
        notifications,
        unreadCount,
        hasMore,
        loadMore,
        loading,
        refetchNotifications,
    } = useMyNotifications();
    const { handleMarkAsRead } = useMarkNotificationAsRead();
    const { handleMarkAllAsRead } = useMarkAllNotificationsAsRead();

    const locale = useLocale();
    const isRtl = locale === 'ar';
    const t = useTranslations("notification");

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen) {
            refetchNotifications();
        }
    }, [isOpen]);

    const handleScroll = () => {
        if (!listRef.current || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = listRef.current;

        if (scrollTop + clientHeight >= scrollHeight - 10) {
            loadMore();
        }
    };

    const handleNotificationClick = async (id: string) => {
        if (markingId === id) return;
        setMarkingId(id);
        try {
            await handleMarkAsRead(id);
            toast.success(t("notificationReadSuccess"));
            refetchNotifications();
        } finally {
            setMarkingId(null);
        }
    };

    const handleMarkAllRead = async () => {
        await handleMarkAllAsRead();
        toast.success(t("allNotificationsReadSuccess"));
        refetchNotifications();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                aria-label={t("notifications")}
                onClick={() => setIsOpen(prev => !prev)}
                className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
            >
                <FaBell className="w-5 h-5 cursor-pointer text-[var(--color-gold-bright)] hover:text-[var(--color-olive-dark)] transition duration-500" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full">
                        {unreadCount}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className={`absolute ${isRtl ? "-left-3 md:left-0" : "-right-3 md:right-0"}  mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50`}
                    >
                        <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="font-semibold text-gray-700">
                                {t("notifications")}
                            </h3>
                            {notifications.some(n => !n.recipients?.[0]?.read) && (
                                <button
                                    onClick={handleMarkAllRead}
                                    className="text-xs text-[var(--color-olive-dark)] hover:text-[var(--color-gold-bright)] flex items-center"
                                >
                                    <FaCheckDouble className="mx-1 text-[var(--color-olive-dark)]" />
                                    {t("markAllAsRead")}
                                </button>
                            )}
                        </div>

                        <div
                            ref={listRef}
                            onScroll={handleScroll}
                            className="max-h-72 overflow-y-auto">
                            {loading ? (
                                <div className="p-4 text-center text-gray-500">
                                    {t("loading")}
                                </div>
                            ) : notifications.length === 0 ? (
                                <div className="p-4 text-center text-gray-500">
                                    {t("noNotifications")}
                                </div>
                            ) : (
                                notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${notification.recipients?.[0]?.read ? 'bg-gray-50' : 'bg-white'}`}
                                        onClick={() => handleNotificationClick(notification.id)}
                                    >
                                        <div className="flex gap-2 items-start">
                                            <Image
                                                width={24}
                                                height={24}
                                                src="/imgs/logo-web.png"
                                                alt="Logo"
                                                className="w-6 h-6 rounded-full object-contain flex-shrink-0 mt-0.5"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-800 break-words">
                                                    {notification.message}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {formatDate(notification.createdAt)}
                                                </p>
                                            </div>
                                            {!notification.recipients?.[0]?.read && (
                                                <FaCheck className="text-[var(--color-olive-dark)] mt-1 flex-shrink-0" />
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <Link
                            href="/profile/notifications"
                            className="flex items-center justify-center p-3 border-t border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                            onClick={() => setIsOpen(false)}
                        >
                            <FaBell className="w-4 h-4 mx-2 text-[var(--color-gold-bright)]" />
                            <span className="text-sm font-medium text-gray-700">
                                {t("viewAllNotifications")}
                            </span>

                            {
                                isRtl ? (
                                    <FaChevronLeft className="w-3 h-3 mr-auto text-gray-400" />
                                ) : (
                                    <FaChevronRight className="w-3 h-3 ml-auto text-gray-400" />
                                )
                            }
                        </Link>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NotificationDropdown;